import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { SkillService } from '../services/skill.service';
import { ToastService } from 'services/toast.service';

import { Client, RootState, Site, UserOfSkillLinked, UserFilter, configService, Option, CommonGlobalService, MultiSelect, LoggerService } from '@umanforge/common';
import { PostSkill } from 'interfaces/api_skill.interfaces';
import { PostClient } from 'interfaces/api_client.interfaces';
import { PostSite } from 'interfaces/api_site.interfaces';

import { DEFAULT_POST_CLIENT, getCreateClient } from '../constants/client.constants';
import { DEFAULT_POST_SITE, getCreateSite } from '../constants/site.constants';
import { DEFAULT_POST_SKILL, MAX_RNCP_LEVEL_SKILL, MIN_RNCP_LEVEL_SKILL, REGEX_RNCP_CODE } from '../constants/skill.constants';
import { ROUTES } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { ILUOSkill } from '../components/users/iluo_skill.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Select } from '../components/inputs/select.component';
import { Loading } from '../components/loading.component';
import { ColorPicker } from '../components/inputs/color_picker.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { UsersIcon } from '../components/icons/users_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';
import { SkillsIcon } from '../components/icons/skills_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { PlusIcon } from '../components/icons/plus_icon.component';

const toastService: ToastService = new ToastService( 'Create Skill' );

type PredefinedSkill = {
  id: string
  name: string
  category: string;
};

export function CreateSkillView() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const logger: LoggerService = new LoggerService( 'CreateSkillView' );

  const jsonSkillDir = 'skill';
  const jsonSkillFilename = 'skill.json';

  const createNewClient: Client = getCreateClient( t );
  const createNewSite: Site = getCreateSite( t );

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ postSkill, setPostSkill ] = useState<PostSkill>( structuredClone( DEFAULT_POST_SKILL ) );
  const [ postSite, setPostSite ] = useState<PostSite>( structuredClone( DEFAULT_POST_SITE ) );
  const [ postClient, setPostClient ] = useState<PostClient>( structuredClone( DEFAULT_POST_CLIENT ) );
  const [ selectedClient, setSelectedClient ] = useState<Option<Client> | undefined>();
  const [ selectedSite, setSelectedSite ] = useState<Option<Site> | undefined>();
  const [ selectedUsers, setSelectedUsers ] = useState<Option<UserOfSkillLinked>[]>( [] );

  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );
  const [ allowedSites, setAllowedSites ] = useState<Option<Site>[]>( [] );

  const [ rerenderClient, setRerenderClient ] = useState<number>( 0 );
  const [ rerenderSite, setRerenderSite ] = useState<number>( 0 );
  const [ rerenderUser, setRerenderUser ] = useState<number>( 0 );

  const [ canPost, setCanPost ] = useState<boolean>( false );
  const [ hasChange, setHasChange ] = useState<boolean>( false );
  const [ useRncp ] = useState<boolean>( false );
  const [ needCreateNewSite, setNeedCreateNewSite ] = useState<boolean>( false );
  const [ needCreateNewClient, setNeedCreateNewClient ] = useState<boolean>( false );

  const [ allPredefinedSkills, setAllPredefinedSkills ] = useState<PredefinedSkill[]>( [] );
  const [ skillCategories, setSkillCategories ] = useState<string[]>( [] );
  const [ selectedCategory, setSelectedCategory ] = useState<string>( '' );

  const [ usePredefinedSkills, setUsePredefinedSkills ] = useState<boolean>( false );
  const [ predefinedSkillsOption, setPredefinedSkillsOption ] = useState<Option<PredefinedSkill>[]>( [] );
  const [ selectedPredefinedSkills, setSelectedPredefinedSkills ] = useState<Option<PredefinedSkill>[]>( [] );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/

  useEffect( () => {
    if ( me && !( CommonGlobalService.isClientAdmin( me ) || CommonGlobalService.isSiteAdmin( me ) ) ) {
      NavigationService.toSkills( navigate, false );
    }
    refreshClients();
  }, [] );

  useEffect( () => {
    if ( usePredefinedSkills ) {
      setIsLoading( true );
      loadSkillsJson().then( skillsList => {
        setAllPredefinedSkills( skillsList );
        const categories = Array.from( new Set( skillsList.map( s => {return s.category;} ) ) ) as string[];
        setSkillCategories( categories );
        setSelectedCategory( '' ); // Ne sélectionne aucune catégorie par défaut
      } ).finally( () => {return setIsLoading( false );} );
    }
  }, [ usePredefinedSkills, i18n.language ] );

  useEffect( () => {
    let filtered = allPredefinedSkills;
    if ( selectedCategory ) {
      filtered = filtered.filter( skill => { return skill.category === selectedCategory; } );
    }
    filtered = filtered.sort( ( a, b ) => { return a.name.localeCompare( b.name ); } );
    setPredefinedSkillsOption( CommonGlobalService.toOptions( filtered ) );
  }, [ selectedPredefinedSkills, allPredefinedSkills, selectedCategory ] );

  useEffect( () => {
    if ( useRncp ) {
      postSkill.rncpCode = postSkill.rncpCode ?? '';
      postSkill.rncpLevel = postSkill.rncpLevel ?? MIN_RNCP_LEVEL_SKILL;
    } else {
      postSkill.rncpCode = undefined;
      postSkill.rncpLevel = undefined;
    }
  }, [ useRncp ] );

  useEffect( () => {
    checkCanPostSkill();
  }, [ postSkill, postSite, postClient, selectedClient, selectedSite, useRncp, usePredefinedSkills, selectedPredefinedSkills ] );

  useEffect( () => {
    checkHasChangeSkill();
  }, [ postSkill, postSite, postClient, selectedClient, selectedSite, useRncp, selectedUsers, usePredefinedSkills, selectedPredefinedSkills ] );

  useEffect( () => {
    setRerenderClient( rerenderClient + 1 );
  }, [ allowedClients ] );

  useEffect( () => {
    setRerenderSite( rerenderSite + 1 );
  }, [ allowedSites ] );

  useEffect( () => {
    if ( selectedClient != null ) {
      refreshSites( selectedClient.object.id );
    }
    postSite.clientId = selectedClient?.value || '';
    setNeedCreateNewClient( selectedClient?.object.id === createNewClient.id );
    setRerenderUser( rerenderUser + 1 );
    setRerenderSite( rerenderSite + 1 );
  }, [ selectedClient ] );

  useEffect( () => {
    setSelectedUsers( [] );
    postSkill.siteId = selectedSite?.value || '';
    setNeedCreateNewSite( selectedClient != null && selectedSite?.object.id === createNewSite.id );
    setPostSkill( structuredClone( postSkill ) );
    if ( selectedSite?.object.id === createNewSite.id ) {
      setSelectedUsers( [] );
    }
  }, [ selectedSite ] );

  useEffect( () => {
    postSkill.users = selectedUsers.map( user => { return { id: user.object.id, value: user.object.value }; } );
    setPostSkill( structuredClone( postSkill ) );
    setRerenderUser( rerenderUser + 1 );
  }, [ selectedUsers ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshClients = async (): Promise<void> => {
    setIsLoading( true );
    const response = await ClientService.getAllowedClients( null, token, dispatch );
    if ( response != null ) {
      if ( me?.isSuperAdmin ) {
        response.unshift( createNewClient );
      }
      const clients = CommonGlobalService.toOptions( response )
        .sort( client => { return client.object.id < createNewClient.id ? 1 : -1; } );
      setAllowedClients( clients );
      if ( clients.length === 1 ) {
        setSelectedClient( clients[ 0 ] );
      }
    }
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const refreshSites = async ( clientId: string ): Promise<void> => {
    let response: Site[] = [];
    if ( clientId != null && clientId != '' && clientId != createNewClient.id ) {
      response = await SiteService.getAllowedSitesByClientsId( [ clientId ], token, dispatch );
    }
    const sitesAdmin = response.filter( site => {
      return ( me && ( CommonGlobalService.isAdminOfSite( me, site.id ) || CommonGlobalService.isAdminOfClient( me, site.clientId ) ) );
    } );
    if ( selectedSite?.object.id != clientId ) {
      setSelectedSite( undefined );
    }
    if ( sitesAdmin != null ) {
      if ( me?.isSuperAdmin ) {
        sitesAdmin.unshift( createNewSite );
      }

      const sites = CommonGlobalService.toOptions( sitesAdmin )
        .sort( site => { return site.object.id < createNewSite.id ? 1 : -1; } );
      setAllowedSites( sites );
      if ( sites.length === 1 ) {
        setSelectedSite( sites[ 0 ] );
      }
    }
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserOfSkillLinked>[] }> => {
    const users: { total: number, list: Option<UserOfSkillLinked>[] } = { total: 0, list: [] };
    if ( selectedSite && selectedSite.object.id !== createNewSite.id ) {
      if ( filter.filterName === undefined || filter.filterName === '' ) {
        delete filter.filterName;
      }
      if ( selectedSite != null ) {
        filter.filterSites = [ selectedSite.object.id ];
      }

      const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
      users.total = result.total;
      users.list = CommonGlobalService.toOptions<UserOfSkillLinked>(
        result.users.map<UserOfSkillLinked>( user => {
          return {
            id: user.id,
            fullname: user.fullname,
            isDisable: user.isDisable,
            isAdminOfUser: user.isAdminOfUser,
            value: 1,
          };
        } )
      );
    }
    return users;
  };

  const resetForm = () => {
    setSelectedUsers( [] );
    setSelectedSite( allowedSites.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedSites[ 0 ] : undefined );
    setSelectedClient( allowedClients.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedClients[ 0 ] : undefined );
    setPostSkill( structuredClone( DEFAULT_POST_SKILL ) );
    setSelectedPredefinedSkills( [] );
    setSelectedCategory( '' );
    setUsePredefinedSkills( false );
  };

  const checkCanPostSkill = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
      ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id )
    );
    const siteChange = (
      ( postSkill.siteId == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
      ( postSkill.siteId != createNewSite.id && postSkill.siteId != null && postSkill.siteId != '' )
    );
    // Color is always valid because DEFAULT_POST_SKILL.color is set by default
    const rncpChange = (
      useRncp == true &&
      postSkill.rncpCode != null && REGEX_RNCP_CODE.test( postSkill.rncpCode ) &&
      postSkill.rncpLevel != null && postSkill.rncpLevel <= MAX_RNCP_LEVEL_SKILL && postSkill.rncpLevel >= MIN_RNCP_LEVEL_SKILL
    ) || useRncp == false;
    let can = false;
    if ( usePredefinedSkills ) {
      can = clientChange && siteChange && selectedPredefinedSkills.length > 0;
    } else {
      const nameChange = postSkill.name != null && postSkill.name != '';
      can = clientChange && siteChange && nameChange && rncpChange;
    }
    setCanPost( can );
  };

  const checkHasChangeSkill = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
      ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id )
    );
    const siteChange = (
      ( postSkill.siteId == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
      ( postSkill.siteId != createNewSite.id && postSkill.siteId != null && postSkill.siteId != '' )
    );
    const nameChange = postSkill.name != null && postSkill.name != '';
    const colorChange = postSkill.color != null && postSkill.color != '' && postSkill.color != DEFAULT_POST_SKILL.color;
    const rncpChange = (
      useRncp == true &&
      postSkill.rncpCode != null && REGEX_RNCP_CODE.test( postSkill.rncpCode ) &&
      postSkill.rncpLevel != null && postSkill.rncpLevel <= MAX_RNCP_LEVEL_SKILL && postSkill.rncpLevel >= MIN_RNCP_LEVEL_SKILL
    );
    const usersChange = selectedUsers.length > 0;

    setHasChange( clientChange || siteChange || nameChange || colorChange || rncpChange || usersChange );
  };

  const toPredefinedSkill = ( json: { data: { name: string; category: string }[] } ): PredefinedSkill[] => {
    if ( !json.data ) {
      return [];
    }

    return json.data.map( item => {
      const name = item.name;
      const category = item.category;

      return {
        id: `${category}-${name}`,
        name,
        category
      };
    } );
  };

  const loadSkillsJson = async (): Promise<PredefinedSkill[]> => {

    const langsToTry = [
      i18n.language,
      await configService.getDefaultLanguage().catch( () => {return 'fr';} )
    ].filter( Boolean ) as string[];

    for ( const lang of langsToTry ) {
      try {
        const res = await fetch( `/${jsonSkillDir}/${lang}/${jsonSkillFilename}` );
        if ( res.ok ) {
          const json = await res.json();
          return toPredefinedSkill( json );
        }
      } catch ( error ) {
        logger.error( `Failed to fetch /${jsonSkillDir}/${lang}/${jsonSkillFilename}`, error );
      }
    }

    return [];
  };


  /*------------------------------ Events -------------------------------*/
  const onCreateClick = async () => {
    setIsLoading( true );
    if ( postSite.clientId === createNewClient.id ) {
      const newClientId = await ClientService.postClient( postClient, token, dispatch );
      if ( newClientId ) {
        postSite.clientId = newClientId;
      }
    }
    if ( postSkill.siteId === createNewSite.id ) {
      const newSiteId = await SiteService.postSite( postSite, token, dispatch );
      if ( newSiteId ) {
        postSkill.siteId = newSiteId;
      }
    }
    if ( postSkill.siteId !== createNewSite.id ) {
      if ( usePredefinedSkills ) {
        let createdCount = 0;
        for ( const skill of selectedPredefinedSkills ) {
          const skillToPost = { ...postSkill, name: skill.label };
          if ( !useRncp ) {
            skillToPost.rncpCode = DEFAULT_POST_SKILL.rncpCode;
            skillToPost.rncpLevel = DEFAULT_POST_SKILL.rncpLevel;
          }
          const isCreated = await SkillService.postSkill( skillToPost, token, dispatch );
          if ( isCreated ) {
            createdCount++;
          }
        }
        if ( createdCount > 0 ) {
          toastService.showSuccess( t( 'All selected skills have been created.' ) );
        }
        resetForm();
      } else {
        if ( !useRncp ) {
          postSkill.rncpCode = DEFAULT_POST_SKILL.rncpCode;
          postSkill.rncpLevel = DEFAULT_POST_SKILL.rncpLevel;
        }
        const isCreated = await SkillService.postSkill( postSkill, token, dispatch );
        if ( isCreated ) {
          toastService.showSuccess( t( 'The {0} skill has been created.' ).replace( '{0}', ( postSkill.name ) ) );
          resetForm();
        }
      }
    }
    setIsLoading( false );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChange } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Create skill' ) } needConfirmationToNav={ hasChange }/>
        <Breadcrumb
          needConfirmationToNav={ hasChange }
          links={
            [
              { label: t( 'common:skill' ), route: ROUTES.SKILLS, icon: ( <SkillsIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: t( 'Create new skill' ), route: ROUTES.NEW_SKILL, icon: ( <PlusIcon className='h-[12px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderClient }
                    disabled={ allowedClients.length === 1 }
                    value={ selectedClient }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ allowedClients }
                    onChange={ option => {
                      setSelectedClient( option );
                    } }
                  />
                  <div className='flex flex-col w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewClient ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_CLIENT_NAME }
                        label={ t( 'Client name' ) }
                        placeholder={ t( 'Client' ) + '...' }
                        icon={ <ClientsIcon className='w-7 h-7' /> }
                        value={ postClient.name }
                        onChange={ newClientName => {
                          postClient.name = newClientName;
                          setPostClient( structuredClone( postClient ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderSite }
                    disabled={ !selectedClient || allowedSites.length === 1 }
                    value={ selectedSite }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ allowedSites }
                    onChange={ option => {
                      setSelectedSite( option );
                    } }
                  />
                  <div className='w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewSite ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_SITE_NAME }
                        label={ t( 'Site name' ) }
                        placeholder={ t( 'Site' ) + '...' }
                        icon={ <SitesIcon className='w-7 h-7' /> }
                        value={ postSite.name }
                        onChange={ newSiteName => {
                          postSite.name = newSiteName;
                          setPostSite( structuredClone( postSite ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='w-full'>
                  {/* Checkbox pour activer le multi-select des compétences pré-enregistrées */}
                  <div className='flex items-center mb-2'>
                    <input
                      type='checkbox'
                      id='predefined-skills-checkbox'
                      checked={ usePredefinedSkills }
                      onChange={ e => {
                        setUsePredefinedSkills( e.target.checked );
                        if ( !e.target.checked ) {
                          setSelectedPredefinedSkills( [] );
                        }
                      } }
                      className='mr-2 h-4 w-4 cursor-pointer'
                    />
                    <label htmlFor='predefined-skills-checkbox' className='cursor-pointer select-none'>
                      {t( 'add_predefined_skills' )}
                    </label>
                  </div>
                  {/* Filtre par catégorie et multi-select */}
                  { usePredefinedSkills ? (
                    <>
                      <div className='mb-2'>
                        <Select
                          label={t( 'translation:category' )}
                          required={false}
                          options={[ { label: t( 'common:all' ), value: '' }, ...skillCategories.map( cat => {return { label: cat, value: cat };} ) ]}
                          value={ selectedCategory !== '' ? { label: selectedCategory, value: selectedCategory } : { label: t( 'common:all' ), value: '' } }
                          onChange={option => { setSelectedCategory( option.value ); }}
                        />
                      </div>
                      <MultiSelect
                        label={ t( 'translation:predefined_skill' ) }
                        placeholder={ t( 'translation:select_skills' ) }
                        icon={<SkillsIcon className='w-7 h-7' />}
                        getValues={ predefinedSkillsOption }
                        selectedValues={ selectedPredefinedSkills }
                        onChange={ setSelectedPredefinedSkills }
                        required
                      />
                    </>
                  ) : (
                    <Input
                      required
                      maxLength={ MAX_LENGTH_ENTITY.UF_SKILL_NAME }
                      label={ t( 'Skill name' ) }
                      placeholder={ t( 'Skill' ) + '...' }
                      icon={ <SkillsIcon className='w-7 h-7' /> }
                      value={ postSkill.name }
                      onChange={ newSkillName => {
                        postSkill.name = newSkillName;
                        setPostSkill( structuredClone( postSkill ) );
                      } }
                    />
                  ) }
                </div>

                <div className='w-1/4 min-w-[225px] self-start'>
                  <ColorPicker
                    label={ usePredefinedSkills ? t( 'translation:predefined_skill_color' ) : t( 'translation:skill_color' ) }
                    value={ postSkill.color }
                    onBlur={ newSkillColor => {
                      postSkill.color = newSkillColor;
                      setPostSkill( structuredClone( postSkill ) );
                    } }
                  />
                </div>

                {/*
                  <div className='w-full'>
                    <div className='-mx-[8px]'>
                      <Button
                        id='rncp-tooltip'
                        label={ t( 'RNCP qualification' ) }
                        color={ useRncp ? '' : '#f87171' }
                        colorHover={ useRncp ? '' : '#ef4444' }
                        icon={ useRncp ? <CheckIcon className='h-5 w-5 fill-white'/> : <CrossIcon className='h-5 w-5 fill-white'/> }
                        onClick={ () => { setUseRncp( !useRncp ); } }
                      />
                      <Tooltip
                        anchorSelect='#rncp-tooltip'
                        className='z-[1000] max-w-[600px] text-wrap'
                        place="right"
                      >
                        <span>
                          { t( 'National Directory of Professional Certifications' ) }
                        </span>
                      </Tooltip>
                    </div>
                    <div className={`flex w-full gap-2 origin-top-left transition-all duration-500 ${useRncp ? 'scale-100 opacity-100 max-h-96' : 'scale-50 opacity-0 max-h-0'}`}>
                      <div className='w-4/5'>
                        <Input
                          key={ useRncp ? 1 : 0 }
                          required
                          onlyUpperCase
                          maxLength={ MAX_LENGTH_ENTITY.UF_SKILL_RNCP_CODE }
                          label={ t( 'RNCP code' ) }
                          placeholder='RNCPXXXXX'
                          icon={ <SkillsIcon className='w-7 h-7' /> }
                          value={ postSkill.rncpCode ?? '' }
                          checkContent={ ( text: string ): string | null => {
                            if ( !REGEX_RNCP_CODE.test( text ) ) {
                              return 'The code format must be RNCPXXXXX';
                            } else {
                              return null;
                            }
                          } }
                          onChange={ newRncpCode => {
                            postSkill.rncpCode = newRncpCode;
                            setPostSkill( structuredClone( postSkill ) );
                          } }
                        />
                      </div>
                      <div className='w-1/5'>
                        <InputNumber
                          key={ useRncp ? 1 : 0 }
                          required
                          min={ MIN_RNCP_LEVEL_SKILL }
                          max={ MAX_RNCP_LEVEL_SKILL }
                          label={ t( 'Level' ) }
                          value={ postSkill.rncpLevel ?? MIN_RNCP_LEVEL_SKILL }
                          onChange={ newRncpLevel => {
                            postSkill.rncpLevel = newRncpLevel;
                            setPostSkill( structuredClone( postSkill ) );
                          } }
                        />
                      </div>
                    </div>
                  </div>
                */}

                <div className='w-full'>
                  <MultiSelect
                    key={ selectedSite?.value }
                    disabled={ !selectedSite || selectedSite.object.id === createNewSite.id }
                    label={ t( 'User' ) }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedUsers }
                    getValues={ getUsers }
                    onChange={ options => {
                      setSelectedUsers( options );
                    } }
                    extra={ user => {
                      return (
                        <ILUOSkill
                          skill={ {
                            id: user.object.id,
                            siteId: postSkill.siteId,
                            name: postSkill.name,
                            rncpCode: postSkill.rncpCode ?? '',
                            rncpLevel: postSkill.rncpLevel ?? MIN_RNCP_LEVEL_SKILL,
                            color: postSkill.color,
                            value: user.object.value
                          } }
                          onChange={ changedSkill => {
                            user.object.value = changedSkill.value;
                            const findedUser = selectedUsers.find( item => { return changedSkill.id == item.object.id;  } );
                            if ( findedUser ) {
                              findedUser.object.value = changedSkill.value;
                            }
                            setSelectedUsers( structuredClone( selectedUsers ) );
                          } }
                        />
                      );
                    } }
                  />
                </div>

                <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
                  <Button
                    label={ t( 'Create' ) }
                    disable={ !canPost }
                    icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                    color='#0bb173'
                    colorHover='#088D5C'
                    onClick={ onCreateClick }
                  />
                </div>
              </form>
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}